import { useCallback, useRef, useState } from "react";

import { sugerirPeriodo } from "@/modules/recursos";
import type { EquipeObraTarget, PapelObra } from "@/modules/recursos";
import type { EquipeRow, RowErrors } from "@/shared/components/equipe-obra/equipe-obra.types";
import {
  EQUIPE_ADDED_TOAST,
  EQUIPE_REMOVED_TOAST,
  EQUIPE_SELECT_PROFISSIONAL_MESSAGE,
  EQUIPE_UPDATED_TOAST,
} from "@/shared/components/equipe-obra/equipe-obra.constants";
import { EQUIPE_NO_ROWS_MESSAGE } from "../definir-equipe-obra.constants";
import { hasErrors, isRowAlterada, rowErrorsFor } from "../definir-equipe-obra.utils";
import type { EquipeObraNotice } from "../types-definir-equipe-obra";

interface UseEquipeLinhasParams {
  obra: EquipeObraTarget;
  /** Período da obra que limita as datas (o da equipe gravada, quando há). */
  periodoObra: { inicio: string; fim: string };
  papeisById: Map<string, PapelObra>;
  onNotify: (notice: EquipeObraNotice) => void;
}

/** Erros que o servidor apontou por linha (índice na lista enviada) no POST da equipe. */
type ErrosDoServidor = Map<
  number,
  { usuarioId?: string; papelObraId?: string; inicio?: string; fim?: string }
>;

/**
 * As linhas da equipe em tela e o aviso do formulário: adicionar, alterar, confirmar, cancelar, remover e
 * validar, mais as intenções que o Salvar usa para refletir o que o servidor respondeu.
 */
export function useEquipeLinhas({
  obra,
  periodoObra,
  papeisById,
  onNotify,
}: UseEquipeLinhasParams) {
  const [linhas, setLinhas] = useState<EquipeRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const counter = useRef(0);

  function patchLinha(id: string, patch: Partial<EquipeRow>, clear: (keyof RowErrors)[] = []) {
    setFormError(null);
    setLinhas((current) =>
      current.map((row) => {
        if (row.id !== id) return row;
        if (row.gravada) {
          // Equipe gravada: valida a cada alteração para o ✓ já nascer desabilitado com erro.
          const next = { ...row, ...patch };
          return { ...next, errors: rowErrorsFor(next, current, periodoObra).errors };
        }
        const errors = { ...row.errors };
        for (const key of clear) delete errors[key];
        return { ...row, ...patch, errors };
      }),
    );
  }

  /** Trocar o papel de uma linha refaz a sugestão de período dele. */
  function alterarLinha(id: string, patch: Partial<EquipeRow>, clear: (keyof RowErrors)[]) {
    const atual = linhas.find((row) => row.id === id);
    if (patch.papelObraId && patch.papelObraId !== atual?.papelObraId) {
      const papel = papeisById.get(patch.papelObraId);
      const periodo = papel ? sugerirPeriodo(papel.janela, obra) : null;
      patchLinha(
        id,
        periodo ? { ...patch, inicio: periodo.inicio, fim: periodo.fim } : patch,
        clear,
      );
      return;
    }
    patchLinha(id, patch, clear);
  }

  function adicionarLinha(papel: PapelObra) {
    counter.current += 1;
    const periodo = sugerirPeriodo(papel.janela, obra);

    setFormError(null);
    setLinhas((current) => [
      ...current,
      {
        id: `linha-${counter.current}`,
        papelObraId: papel.id,
        usuarioId: null,
        inicio: periodo.inicio,
        fim: periodo.fim,
        editing: true,
        isNew: true,
        backup: null,
        errors: {},
      },
    ]);
  }

  function confirmarLinha(id: string) {
    const row = linhas.find((candidate) => candidate.id === id);
    if (!row) return;

    const { errors } = rowErrorsFor(row, linhas, periodoObra);
    if (hasErrors(errors)) {
      setLinhas((current) => current.map((r) => (r.id === id ? { ...r, errors } : r)));
      return;
    }

    setLinhas((current) =>
      current.map((r) =>
        r.id === id ? { ...r, editing: false, isNew: false, backup: null, errors: {} } : r,
      ),
    );
    // Na equipe gravada o ✓ só confirma na tela; o aviso vem ao Salvar.
    if (!row.gravada) {
      onNotify({
        severity: "success",
        summary: row.isNew ? EQUIPE_ADDED_TOAST : EQUIPE_UPDATED_TOAST,
      });
    }
  }

  function cancelarLinha(id: string) {
    setLinhas((current) =>
      current.flatMap((row) => {
        if (row.id !== id) return [row];
        if (row.isNew || !row.backup) return [];
        return [
          { ...row, ...row.backup, editing: false, backup: null, errors: {}, saveFailed: false },
        ];
      }),
    );
  }

  function editarLinha(id: string) {
    setLinhas((current) =>
      current.map((row) =>
        row.id === id
          ? {
              ...row,
              editing: true,
              backup: { usuarioId: row.usuarioId, inicio: row.inicio, fim: row.fim },
            }
          : row,
      ),
    );
  }

  function removerLinha(id: string) {
    const row = linhas.find((candidate) => candidate.id === id);
    setLinhas((current) => current.filter((candidate) => candidate.id !== id));
    setFormError(null);
    if (row?.usuarioId) onNotify({ severity: "success", summary: EQUIPE_REMOVED_TOAST });
  }

  /** Valida todas as linhas antes de qualquer requisição; devolve se estão válidas. */
  function validarTudo(): boolean {
    if (linhas.length === 0) {
      setFormError(EQUIPE_NO_ROWS_MESSAGE);
      return false;
    }

    let missing = false;
    const next = linhas.map((row) => {
      // Gravada sem alteração não é enviada: pode estar fora do período atual da obra.
      if (row.gravada && !isRowAlterada(row)) return row;
      const result = rowErrorsFor(row, linhas, periodoObra);
      if (result.missingProfissional) missing = true;
      // A leitura não mostra erro de data (nem o do profissional, fora da equipe gravada): a linha volta à
      // edição para exibi-lo.
      if (!row.editing && hasErrors(result.errors)) {
        return {
          ...row,
          editing: true,
          backup: { usuarioId: row.usuarioId, inicio: row.inicio, fim: row.fim },
          errors: result.errors,
        };
      }
      return { ...row, errors: result.errors };
    });

    setLinhas(next);
    setFormError(missing ? EQUIPE_SELECT_PROFISSIONAL_MESSAGE : null);

    return next.every((row) => !hasErrors(row.errors));
  }

  /** O aviso do formulário (o Toast da tela fica atrás do modal). */
  const avisar = useCallback((mensagem: string) => {
    setFormError(mensagem);
  }, []);

  /** As alocações da equipe gravada viram as linhas. */
  const carregarLinhas = useCallback((carregadas: EquipeRow[]) => {
    setLinhas(carregadas);
  }, []);

  /** O PATCH da linha deu certo: ela volta à leitura com o período e a versão gravados. */
  const registrarGravada = useCallback(
    (id: string, gravado: { inicio: string; fim: string; updatedAt?: string }) => {
      const { inicio, fim, updatedAt } = gravado;
      setLinhas((current) =>
        current.map((item) =>
          item.id === id
            ? {
                ...item,
                inicio,
                fim,
                base: { inicio, fim },
                updatedAt: updatedAt || item.updatedAt,
                editing: false,
                backup: null,
                errors: {},
                saveFailed: false,
              }
            : item,
        ),
      );
    },
    [],
  );

  /** O servidor recusou as datas da linha: ela volta à edição com o erro no campo. */
  const reabrirComErro = useCallback((id: string, errors: { inicio?: string; fim?: string }) => {
    setLinhas((current) =>
      current.map((item) =>
        item.id === id
          ? {
              ...item,
              editing: true,
              backup: { usuarioId: item.usuarioId, ...(item.base ?? item) },
              errors,
              saveFailed: true,
            }
          : item,
      ),
    );
  }, []);

  /** O PATCH da linha falhou: ela fica marcada até a próxima tentativa. */
  const marcarFalha = useCallback((id: string) => {
    setLinhas((current) =>
      current.map((item) => (item.id === id ? { ...item, saveFailed: true } : item)),
    );
  }, []);

  /** O POST da equipe recusou campos: o erro vai para a linha de mesmo índice. */
  const aplicarErrosDoServidor = useCallback((erros: ErrosDoServidor) => {
    setLinhas((current) =>
      current.map((row, index) => {
        const fields = erros.get(index);
        if (!fields) return row;
        return {
          ...row,
          errors: {
            ...row.errors,
            usuario: fields.usuarioId ?? fields.papelObraId ?? row.errors.usuario,
            inicio: fields.inicio ?? row.errors.inicio,
            fim: fields.fim ?? row.errors.fim,
          },
        };
      }),
    );
  }, []);

  return {
    linhas,
    formError,
    alterarLinha,
    adicionarLinha,
    confirmarLinha,
    cancelarLinha,
    editarLinha,
    removerLinha,
    validarTudo,
    avisar,
    carregarLinhas,
    registrarGravada,
    reabrirComErro,
    marcarFalha,
    aplicarErrosDoServidor,
  };
}
